import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AlistamientoOrden, LineaAlistamiento } from '@manejo-alcanza/contracts';
import {
  alistamientoDeOrden,
  cantidad as formatoCantidad,
  despacharOrden,
  fecha,
  ordenesPorAlistar,
} from '../api';
import { Aviso, Card, Cargando, Error_ } from '../componentes/basicos';

/**
 * F4 — alistar y despachar. **Diseñado para el celular.**
 *
 * *(Pedro, 19/09/2026: «más bien pensado para celular pero que también permita
 * computador».)* Quien lo usa está de pie en la bodega con canastillas en las
 * manos, no sentado frente a un monitor.
 *
 * Eso decide cada detalle de aquí:
 *
 * - **Tarjetas, no tablas.** En 360 px una tabla de seis columnas no se lee.
 * - **`−` y `+` antes que teclear.** Escribir un número con las manos ocupadas
 *   es justo donde se digita mal.
 * - **Objetivos de 44 px.** Un dedo no es un puntero.
 * - **El botón de despachar, abajo y fijo.** Es donde llega el pulgar.
 * - **En pantalla ancha las tarjetas se reparten**, y es la misma pantalla: no
 *   hay una versión de escritorio aparte que mantener.
 *
 * ## Alistar no es despachar
 *
 * Contar y apartar no mueve inventario: la mercancía sigue en la bodega. **Lo
 * único que escribe en el libro es que el camión salga.**
 */
export function Alistamiento(): ReactNode {
  const consulta = useQuery({ queryKey: ['alistamiento'], queryFn: ordenesPorAlistar });

  if (consulta.isLoading) return <Cargando que="las órdenes por alistar" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const ordenes = consulta.data!;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Alistar</h1>
        <p className="mt-1 text-tenue">
          Lo que está por salir de la bodega. Toca una orden para ver qué hay que recoger.
        </p>
      </header>

      {ordenes.length === 0 ? (
        <Card>
          <p className="text-sm text-tenue">
            No hay nada por alistar. Aquí aparecen las órdenes que todavía no se han
            despachado.
          </p>
        </Card>
      ) : (
        // Una columna en el teléfono, dos o tres cuando hay sitio. La misma
        // pantalla, no otra.
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {ordenes.map((o) => (
            <li key={o.ordenId}>
              <Link
                to={`/alistar/${o.ordenId}`}
                // min-h-[44px] no: la tarjeta entera es el objetivo tactil, y
                // es mucho mas grande que el minimo.
                className="block rounded-lg border border-borde bg-superficie p-4 active:bg-neutro-tenue"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-lg font-semibold">{o.numero}</span>
                  <span className="text-sm text-tenue">{fecha(o.fechaEntrega)}</span>
                </div>
                <div className="mt-1 text-sm text-tenue">{o.clienteNombre}</div>
                <div className="mt-2 text-sm">
                  {o.lineas} {o.lineas === 1 ? 'producto' : 'productos'} ·{' '}
                  {o.destinos} {o.destinos === 1 ? 'tienda' : 'tiendas'}
                </div>
                {o.faltaInventario && (
                  <div className="mt-2 rounded bg-aviso-tenue px-2 py-1 text-xs text-aviso">
                    El saldo de bodega no alcanza para todo
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** El detalle: qué recoger, cuánto va para cada tienda, y el despacho. */
export function AlistarOrden(): ReactNode {
  const { id = '' } = useParams();
  const cliente = useQueryClient();

  const consulta = useQuery({
    queryKey: ['alistamiento', id],
    queryFn: () => alistamientoDeOrden(id),
  });

  /** Lo que se lleva de verdad, por línea. Arranca en lo pedido. */
  const [cargado, setCargado] = useState<Record<string, number>>({});

  const despachar = useMutation({
    mutationFn: (a: AlistamientoOrden) =>
      despacharOrden(id, {
        ubicacion: a.ubicacion,
        fecha: hoy(),
        lineas: a.lineas.map((l) => ({
          lineaId: l.lineaId,
          cantidad: cargado[l.lineaId] ?? Number(l.cantidadPedida),
        })),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['alistamiento'] });
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
    },
  });

  if (consulta.isLoading) return <Cargando que="el alistamiento" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const a = consulta.data!;
  const yaSalio = a.despachadaEn !== null;

  return (
    // El relleno de abajo deja sitio a la barra fija: sin el, la ultima tarjeta
    // queda debajo del boton y no se puede leer.
    <div className="space-y-4 pb-28">
      <header>
        <Link to="/alistar" className="text-sm text-acento underline underline-offset-2">
          ← Todas las órdenes
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{a.numero}</h1>
        <p className="mt-1 text-tenue">
          {a.clienteNombre} · entrega {fecha(a.fechaEntrega)}
        </p>
      </header>

      {yaSalio && (
        <Aviso>
          Esta orden ya se despachó{a.despachadaPor ? `, la cargó ${a.despachadaPor}` : ''}. Si
          algo salió mal, se corrige movimiento por movimiento desde el inventario.
        </Aviso>
      )}

      {despachar.isError && <Error_>{(despachar.error as Error).message}</Error_>}
      {despachar.isSuccess && (
        <Aviso>
          Despachada: {despachar.data.movimientos}{' '}
          {despachar.data.movimientos === 1 ? 'movimiento' : 'movimientos'} en el libro.
        </Aviso>
      )}

      <ul className="grid gap-3 lg:grid-cols-2">
        {a.lineas.map((l) => (
          <li key={l.lineaId}>
            <TarjetaLinea
              linea={l}
              cargado={cargado[l.lineaId] ?? Number(l.cantidadPedida)}
              alCambiar={(n) => setCargado((c) => ({ ...c, [l.lineaId]: n }))}
              bloqueada={yaSalio || despachar.isSuccess}
            />
          </li>
        ))}
      </ul>

      {!yaSalio && !despachar.isSuccess && (
        /*
         * Fijo abajo: es donde llega el pulgar sin cambiar la mano de sitio.
         * `safe-area-inset-bottom` deja libre la barra del iPhone.
         */
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-borde bg-superficie/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-12px_rgb(6_41_63/0.25)] backdrop-blur lg:left-64">
          <div className="mx-auto max-w-7xl">
            <button
              type="button"
              onClick={() => despachar.mutate(a)}
              disabled={despachar.isPending}
              className="min-h-[52px] w-full rounded-lg bg-acento px-4 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {despachar.isPending ? 'Registrando…' : 'El camión salió'}
            </button>
            <p className="mt-1.5 text-center text-xs text-tenue">
              Esto descuenta del inventario. Alistar no: solo salir del patio.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TarjetaLinea({
  linea: l,
  cargado,
  alCambiar,
  bloqueada,
}: {
  linea: LineaAlistamiento;
  cargado: number;
  alCambiar: (n: number) => void;
  bloqueada: boolean;
}): ReactNode {
  const pedida = Number(l.cantidadPedida);
  const difiere = cargado !== pedida;

  return (
    <div className="rounded-lg border border-borde bg-superficie p-4">
      <div className="text-base font-medium">{l.nombre}</div>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-sm text-tenue">
        <span>
          pedido: <strong className="text-texto">{formatoCantidad(l.cantidadPedida)}</strong>{' '}
          {l.unidad}
        </span>
        {/*
          El saldo en null es «no se» y no «cero»: la linea no se resolvio a
          ninguna presentacion, asi que no hay de que mirar el saldo.
        */}
        <span>
          en bodega:{' '}
          {l.saldo === null ? (
            <span className="italic">no se sabe</span>
          ) : (
            <strong className={l.alcanza === false ? 'text-alerta' : 'text-texto'}>
              {formatoCantidad(l.saldo)}
            </strong>
          )}
        </span>
      </div>

      {l.alcanza === false && (
        <p className="mt-2 rounded bg-aviso-tenue px-2 py-1 text-xs text-aviso">
          No alcanza. Puede que falte registrar una producción — el despacho no se bloquea.
        </p>
      )}

      {!bloqueada && (
        <div className="mt-3">
          <span className="mb-1.5 block text-xs uppercase tracking-wide text-tenue">
            Lo que se cargó
          </span>
          <Contador valor={cargado} alCambiar={alCambiar} />
          {difiere && (
            <p className="mt-1.5 text-xs text-tenue">
              {cargado < pedida ? 'Van' : 'Van'} {formatoCantidad(String(cargado))} de{' '}
              {formatoCantidad(l.cantidadPedida)}. Se registra lo que salió.
            </p>
          )}
        </div>
      )}

      {l.destinos.length > 0 && <Destinos destinos={l.destinos} />}
    </div>
  );
}

/**
 * Un contador con `−` y `+`, y el número tecleable si hace falta.
 *
 * Los botones son lo primero porque teclear con canastillas en las manos es
 * justo donde se equivoca; el campo queda para cuando hay que poner 96 de golpe.
 */
function Contador({
  valor,
  alCambiar,
}: {
  valor: number;
  alCambiar: (n: number) => void;
}): ReactNode {
  const boton =
    'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-borde ' +
    'text-xl font-semibold active:bg-neutro-tenue disabled:opacity-40';

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label="Quitar uno"
        className={boton}
        disabled={valor <= 0}
        onClick={() => alCambiar(Math.max(0, valor - 1))}
      >
        −
      </button>
      <input
        // `inputMode` saca el teclado numerico en el telefono; `type=text` para
        // que no aparezcan las flechitas del navegador, que son diminutas.
        type="text"
        inputMode="decimal"
        value={valor}
        onChange={(e) => {
          const n = Number(e.target.value.replace(',', '.'));
          if (!Number.isNaN(n) && n >= 0) alCambiar(n);
        }}
        className="cifra h-12 w-full min-w-0 rounded-lg border border-borde px-3 text-center text-lg"
      />
      <button
        type="button"
        aria-label="Agregar uno"
        className={boton}
        onClick={() => alCambiar(valor + 1)}
      >
        +
      </button>
    </div>
  );
}

/** Cuánto va para cada tienda. Es la instrucción de alistamiento. */
function Destinos({
  destinos,
}: {
  destinos: LineaAlistamiento['destinos'];
}): ReactNode {
  const [abierto, setAbierto] = useState(false);

  return (
    <div className="mt-3 border-t border-borde pt-2">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex min-h-[44px] w-full items-center justify-between text-sm"
      >
        <span>
          Para {destinos.length} {destinos.length === 1 ? 'tienda' : 'tiendas'}
        </span>
        <span className="text-tenue">{abierto ? 'ocultar' : 'ver'}</span>
      </button>

      {abierto && (
        <ul className="mt-1 space-y-1 text-sm">
          {destinos.map((d, i) => (
            <li key={`${d.codigo}-${i}`} className="flex justify-between gap-2">
              <span>
                {d.codigo && <span className="font-mono text-xs">{d.codigo}</span>}{' '}
                {d.nombre ?? <span className="text-tenue italic">sin identificar</span>}
              </span>
              <span className="cifra">{d.cantidad}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Hoy en Bogotá. La fecha del despacho es la del día, no la de la orden. */
function hoy(): string {
  return new Date(Date.now() - 5 * 3600e3).toISOString().slice(0, 10);
}
